/* eslint-disable react/jsx-filename-extension */
import { List, Map } from 'immutable';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import r from 'render-dom';

import { addAlert } from 'components/shared/Alert';
import { STATE } from 'constants/campaigns';
import { primary1Color, primary2Color } from 'constants/colors';
import { hideCampaign } from 'redux/campaigns/actions';
import { saveEmailSettings } from 'redux/emailSettings/actions';
import { saveListing } from 'redux/listing/actions';
import { savePage } from 'redux/page/actions';
import { saveProduct } from 'redux/product/actions';
import { addDialog, removeDialog } from 'redux/ui/actions';

import {
    CREATE_MULTI_USE_PROMOTION,
    CREATE_MULTI_USE_PROMOTION_FAIL,
    CREATE_MULTI_USE_PROMOTION_SUCCESS,
    CREATE_PROMOTIONS,
    CREATE_PROMOTIONS_FAIL,
    CREATE_PROMOTIONS_SUCCESS,
    DELETE_MULTI_USE_PROMOTION,
    DELETE_MULTI_USE_PROMOTION_FAIL,
    DELETE_MULTI_USE_PROMOTION_SUCCESS,
    DELETE_SINGLE_USE_PROMOTION,
    DELETE_SINGLE_USE_PROMOTION_FAIL,
    DELETE_SINGLE_USE_PROMOTION_SUCCESS,
    EDIT_IN_CAMPAIGN,
    EDIT_MULTI_USE_PROMOTION,
    FETCH_CAMPAIGN,
    FETCH_CAMPAIGN_FAIL,
    FETCH_CAMPAIGN_STATS,
    FETCH_CAMPAIGN_STATS_FAIL,
    FETCH_CAMPAIGN_STATS_SUCCESS,
    FETCH_CAMPAIGN_SUCCESS,
    FUND_CAMPAIGN,
    FUND_CAMPAIGN_FAIL,
    FUND_CAMPAIGN_SUCCESS,
    RESET_CAMPAIGN,
    TOGGLE_EDIT_MULTI_USE_PROMOTION,
    UPDATE_CAMPAIGN,
    UPDATE_CAMPAIGN_DISCOUNT_TYPE,
    UPDATE_CAMPAIGN_FAIL,
    UPDATE_CAMPAIGN_PERCENT_OFF,
    UPDATE_CAMPAIGN_SUCCESS,
    UPDATE_CAMPAIGN_TYPE,
    UPDATE_MULTI_USE_PROMOTION,
    UPDATE_MULTI_USE_PROMOTION_FAIL,
    UPDATE_MULTI_USE_PROMOTION_SUCCESS,
    VERIFY_BOOST,
    VERIFY_BOOST_FAIL,
    VERIFY_BOOST_SUCCESS,
} from './reducer';

const embeds = {
    embeds: [
        'action',
        'applications',
        'boost',
        'emailSettings',
        'features',
        'followups',
        'listing',
        'multiUsePromotion',
        'page.medium',
        'product.categories',
        'product.media',
        'rewardCodes',
        'singleUsePromotions.order',
    ].join(','),
};
const getEmbeds = (forAdmin) => {
    if (forAdmin) {
        embeds.embeds += ',company.activeSubscription';
    }
    return embeds;
};

export const editInCampaign = (path) => (value) => ({
    path,
    type: EDIT_IN_CAMPAIGN,
    value,
});

export const editCampaignField = (field) => editInCampaign(['data', field]);

export const editCampaign = (field, value) => editCampaignField(field)(value);

export const setCampaignIsSaving = editInCampaign(['isSaving']);

export const fetchCampaign = (id, forAdmin) => ({
    payload: {
        request: {
            params: getEmbeds(forAdmin),
            url: `/campaigns/${id}`,
        },
    },
    types: [FETCH_CAMPAIGN, FETCH_CAMPAIGN_SUCCESS, FETCH_CAMPAIGN_FAIL],
});

export const fetchCampaignStats = (id) => ({
    payload: { request: { url: `/campaigns/${id}/stats` } },
    types: [FETCH_CAMPAIGN_STATS, FETCH_CAMPAIGN_STATS_SUCCESS, FETCH_CAMPAIGN_STATS_FAIL],
});

export const createCampaign = (data) => ({
    payload: {
        request: {
            data,
            method: 'post',
            params: embeds,
            url: '/campaigns',
        },
    },
    types: [UPDATE_CAMPAIGN, UPDATE_CAMPAIGN_SUCCESS, UPDATE_CAMPAIGN_FAIL],
});

export const fundCampaign = (id) => ({
    payload: {
        request: {
            method: 'post',
            url: `/campaigns/${id}/fund`,
        },
    },
    types: [FUND_CAMPAIGN, FUND_CAMPAIGN_SUCCESS, FUND_CAMPAIGN_FAIL],
});

export const updateCampaign = (data) => ({
    payload: {
        request: {
            data: data.toJS(),
            method: 'put',
            params: embeds,
            url: `/campaigns/${data.get('id')}`,
        },
    },
    types: [UPDATE_CAMPAIGN, UPDATE_CAMPAIGN_SUCCESS, UPDATE_CAMPAIGN_FAIL],
});

export function patchCampaign(campaign, data) {
    return {
        payload: {
            request: {
                data,
                method: 'patch',
                params: embeds,
                url: `/campaigns/${campaign.get('id')}`,
            },
        },
        types: [UPDATE_CAMPAIGN, UPDATE_CAMPAIGN_SUCCESS, UPDATE_CAMPAIGN_FAIL],
    };
}

export const saveCampaign = (campaign) => (dispatch) => {
    const boost = campaign.get('boost');
    const boostCompleted = boost
        && boost.get('queries').filter((query) => query.length).size > 0;
    const emailSettings = campaign.get('emailSettings');
    const listing = campaign.get('listing');
    const page = campaign.get('page');
    const product = campaign.get('product');
    const promises = [];
    const mockApiResponse = (data) => ({ payload: { data } });
    const saveIfCampaignIdExists = (map, saveFn) => {
        if (map && map.get('campaignId')) {
            return dispatch(saveFn(map));
        }

        return Promise.resolve(mockApiResponse(map));
    };

    dispatch(setCampaignIsSaving(true));

    promises.push(
        dispatch(saveListing(listing))
    );

    promises.push(
        dispatch(saveProduct(product))
    );

    promises.push(saveIfCampaignIdExists(page, savePage));

    promises.push(saveIfCampaignIdExists(emailSettings, saveEmailSettings));

    return new Promise((resolve) => {
        Promise.all(promises).then((responses) => {
            const [savedListing, savedProduct, savedPage, savedEmailSettings] = responses;
            const campaignWithSavedEmbeds = campaign
                .set('boost', boostCompleted ? boost : null)
                .set('listing', savedListing.payload.data)
                .set('product', savedProduct.payload.data)
                .set('page', savedPage.payload.data)
                .set('emailSettings', savedEmailSettings.payload.data);
            const action = campaignWithSavedEmbeds.get('id') ? updateCampaign : createCampaign;

            resolve(
                dispatch(action(campaignWithSavedEmbeds))
            );
        }).catch((err) => {
            dispatch(setCampaignIsSaving(false));
            return Promise.reject(err);
        });
    });
};

export const verifyBoost = (campaign) => ({
    payload: {
        request: {
            data: { campaignId: campaign.get('id') },
            method: 'post',
            url: '/boosts/verify',
        },
    },
    types: [
        VERIFY_BOOST,
        VERIFY_BOOST_SUCCESS,
        VERIFY_BOOST_FAIL,
    ],
});

export const startCampaign = (campaign) => (dispatch) => {
    return new Promise((resolve, reject) => {
        dispatch(
            patchCampaign(campaign, { state: STATE.PUBLISHED })
        ).then(() => {
            return resolve();
        },
        (e) => { return reject(e); });
    });
};

export const saveAndPublishCampaign = (campaign) => (dispatch) => {
    return new Promise((resolve, reject) => {
        dispatch(saveCampaign(campaign)).then(() => {
            // if (campaign.getIn(['action', 'type']) !== 'RESEARCH') {
            if (campaign.getIn(['multiUsePromotion', 'ccFunded']) && !campaign.get('isFunded')) {
                return dispatch(fundCampaign(campaign.get('id'))).then(
                    () => {
                        return dispatch(startCampaign(campaign)).then(() => {
                            resolve();
                        });
                    },
                    (e) => { return reject(e); }
                );
            }
            // }

            return dispatch(startCampaign(campaign)).then(() => {
                resolve();
            });
        });
    });
};

export const saveAndVerifyBoost = (campaign) => (dispatch) => dispatch(
    saveCampaign(campaign)
).then(() => dispatch(verifyBoost(campaign)));

export const updateCampaignType = (campaignType) => ({
    campaignType,
    type: UPDATE_CAMPAIGN_TYPE,
});

export const handleCampaignAction = ({ action }, campaign) => (dispatch) => {
    dispatch(action(campaign));
};

export const resetCampaign = () => ({ type: RESET_CAMPAIGN });

export const updateCampaignDiscountType = (discountType) => ({
    discountType,
    type: UPDATE_CAMPAIGN_DISCOUNT_TYPE,
});

export const updateCampaignPercentOff = (percent) => ({
    percent,
    type: UPDATE_CAMPAIGN_PERCENT_OFF,
});

export const duplicateCampaign = (campaign) => (dispatch) => {
    const applications = campaign.get('applications', List()).map(
        (application) => application.deleteIn(['pivot', 'campaign_id'])
    ).toJS();
    const setCampaignEmbedIds = (c) => {
        // const boost = c.get('boost', Map({})) || Map({});

        return Map({
            // boost: boost.delete('id').delete('campaignId'),
            boost: null,
            listingId: c.getIn(['listing', 'id']),
            pageId: c.getIn(['page', 'id']),
            productId: c.getIn(['product', 'id']),
        }).merge(c).toJS();
    };

    dispatch(
        createCampaign({
            ...setCampaignEmbedIds(campaign),
            applications,
            endsAt: null,
            name: `COPY ${campaign.get('name')}`,
            startsAt: null,
            state: STATE.DRAFT,
            type: campaign.get('type'),
        })
    ).then((res) => {
        if (!res.error) {
            return addAlert({
                autoHideDuration: 3000,
                message: 'Your campaign was duplicated!',
            });
        }

        return addAlert({
            autoHideDuration: 5000,
            message: 'There was an error duplicating your campaign!',
        });
    });
};

export const relaunchCampaign = (campaign) => {
    const applications = campaign.get('applications', List([])).map(
        (application) => application.deleteIn(['pivot', 'campaign_id'])
    ).toJS();
    const setCampaignEmbedIds = (c) => {
        const boost = c.get('boost', Map({})) || Map({});

        return Map({
            boost,
            listingId: c.getIn(['listing', 'id']),
            pageId: c.getIn(['page', 'id']),
            productId: c.getIn(['product', 'id']),
        }).merge(c).toJS();
    };

    return createCampaign({
        ...setCampaignEmbedIds(campaign),
        applications,
        endsAt: null,
        id: null,
        name: `RELAUNCH ${campaign.get('name')}`,
        startsAt: null,
        state: STATE.DRAFT,
        type: campaign.get('type'),
    });
};

export const archiveCampaign = (campaign) => (dispatch) => {
    dispatch(
        patchCampaign(campaign, { state: STATE.ARCHIVED })
    );
    dispatch(
        hideCampaign(campaign)
    );
};

export const endCampaign = (campaign) => (dispatch) => {
    const dialog = {
        actions: [],
        children: (
            r.div([
                r.p([
                    'Are you sure you want to permanently end this campaign?'
                ]),
                r.p([
                    `Shoppers will no longer have access to your offer.
                    If you would like to launch another similar campaign you can
                    duplicate the campaign from “My Campaigns”.`
                ]),
            ])
        ),
        id: Symbol('end-campaign-dialog'),
        title: 'End this Campaign?',
    };
    dialog.actions.push(
        r(FlatButton, {
            label: 'Cancel',
            onClick: () => dispatch(removeDialog(dialog.id)),
        }),
        r(RaisedButton, {
            backgroundColor: primary1Color,
            label: 'End Campaign',
            onClick: () => {
                dispatch(
                    patchCampaign(campaign, { state: STATE.STOPPED })
                );

                dispatch(removeDialog(dialog.id));
            },
        }),
        r(RaisedButton, {
            backgroundColor: primary2Color,
            label: 'Archive Campaign',
            onClick: () => {
                dispatch(removeDialog(dialog.id));
                dispatch(archiveCampaign(campaign));
            },
        })
    );

    addDialog(dialog);
};

export const pauseCampaign = (campaign) => (dispatch) => {
    const dialog = {
        actions: [],
        children: (
            r.div([
                r.p(['Are you sure you want to pause this campaign?']),
            ])
        ),
        id: 'pause-campaign-dialog',
        title: Symbol('Pause this Campaign?'),
    };
    dialog.actions.push(
        r(FlatButton, {
            label: 'Cancel',
            onClick: () => dispatch(removeDialog(dialog.id))
        }),
        r(RaisedButton, {
            backgroundColor: primary1Color,
            label: 'Pause Campaign',
            onClick: () => {
                dispatch(
                    patchCampaign(campaign, { state: STATE.PAUSED })
                );

                dispatch(removeDialog(dialog.id));
            }
        })
    );

    addDialog(dialog);
};

export const createPromotions = (data) => ({
    payload: {
        request: {
            data,
            method: 'post',
            url: '/promotions',
        },
    },
    types: [CREATE_PROMOTIONS, CREATE_PROMOTIONS_SUCCESS, CREATE_PROMOTIONS_FAIL],
});

const deletePromotion = (promotion, types, failMessage) => ({
    failMessage,
    payload: {
        request: {
            method: 'delete',
            url: `/promotions/${promotion.get('id')}`,
        },
    },
    promotion,
    types,
});

export const deleteMultiUsePromotion = (promotion) => deletePromotion(
    promotion,
    [
        DELETE_MULTI_USE_PROMOTION,
        DELETE_MULTI_USE_PROMOTION_SUCCESS,
        DELETE_MULTI_USE_PROMOTION_FAIL,
    ]
);

export const deleteSingleUsePromotion = (promotion) => deletePromotion(
    promotion,
    [
        DELETE_SINGLE_USE_PROMOTION,
        DELETE_SINGLE_USE_PROMOTION_SUCCESS,
        DELETE_SINGLE_USE_PROMOTION_FAIL,
    ]
);

export const editMultiUsePromotion = (field, value) => ({
    field,
    type: EDIT_MULTI_USE_PROMOTION,
    value,
});

export const toggleEditMultiUsePromotion = () => ({ type: TOGGLE_EDIT_MULTI_USE_PROMOTION });

export const createMultiUsePromotion = (data) => ({
    payload: {
        request: {
            data,
            method: 'post',
            url: '/promotions',
        },
    },
    types: [
        CREATE_MULTI_USE_PROMOTION,
        CREATE_MULTI_USE_PROMOTION_SUCCESS,
        CREATE_MULTI_USE_PROMOTION_FAIL,
    ],
});

export const updateMultiUsePromotion = (data) => ({
    payload: {
        request: {
            data,
            method: 'put',
            url: `/promotions/${data.get('id')}`,
        },
    },
    types: [
        UPDATE_MULTI_USE_PROMOTION,
        UPDATE_MULTI_USE_PROMOTION_SUCCESS,
        UPDATE_MULTI_USE_PROMOTION_FAIL,
    ],
});

export const saveMultiUsePromotion = (data) => (dispatch) => {
    return data.get('id')
        ? dispatch(updateMultiUsePromotion(data))
        : dispatch(createMultiUsePromotion(data));
};

export const dispatchAction = (action) => action;

export const disableSave = (value) => ({
    path: ['disableSave'],
    type: EDIT_IN_CAMPAIGN,
    value,
});

export const setError = (name, value) => ({
    path: ['errors', name],
    type: EDIT_IN_CAMPAIGN,
    value,
});
